/**
 * Telegram launch parameters, captured before anything else runs.
 *
 * Telegram hands a Mini App its launch data in the URL **hash**, not the query
 * string:
 *
 *   #tgWebAppData=…&tgWebAppVersion=7.10&tgWebAppPlatform=ios&tgWebAppThemeParams=…
 *
 * `telegram-web-app.js` parses the same hash into `window.Telegram.WebApp`, and
 * — verified against the SDK source, not assumed — it also mirrors every launch
 * parameter into `sessionStorage` under `__telegram__initParams` and restores
 * them when a later load arrives with no hash. So the plan's stated worry, that
 * React Router eats the hash on the first navigation, is already covered by the
 * SDK.
 *
 * This module exists for the two things the SDK does NOT cover:
 *
 *  1. **It answers `isMiniApp()` even when the SDK is not there.**
 *     `telegram-web-app.js` is a third-party script over the network. If it is
 *     blocked, slow, or fails, `window.Telegram` is undefined and the app has
 *     no idea it is running inside Telegram — it would render the OIDC login
 *     page inside a webview that cannot complete an OIDC redirect. The hash is
 *     on the URL either way.
 *  2. **It is pure and therefore testable.** "Silently decided it wasn't a Mini
 *     App" is a failure that presents as a login loop, which is exactly the
 *     kind of thing that should be pinned by a test rather than discovered on
 *     somebody's phone.
 *
 * `captureLaunchParams()` is called as the FIRST statement in `main.tsx`, above
 * `createBrowserRouter`. Nothing here touches the DOM beyond `location` and
 * `sessionStorage`, and every storage access is wrapped — Safari private mode
 * and sandboxed iframes both throw on `sessionStorage`, and an exception there
 * must degrade to "no Mini App", never to a blank page.
 */

/** Our own key. Deliberately not Telegram's `__telegram__initParams`. */
const STORAGE_KEY = 'nn.telegram.launchParams';

export interface LaunchParams {
  /**
   * Raw `tgWebAppData` — the string the server HMACs. One layer of URL
   * decoding has been applied (the hash's own), leaving each field's inner
   * encoding intact. Re-encoding or re-serialising this invalidates the hash.
   */
  initData: string | null;
  /**
   * Bot API version of the client. `'6.0'` when absent, matching the SDK's own
   * default — every capability gate treats an unknown client as the oldest
   * one rather than the newest.
   */
  version: string;
  /** `'ios' | 'android' | 'tdesktop' | 'weba' | 'webk' | …`, or `'unknown'`. */
  platform: string;
}

const ABSENT: LaunchParams = { initData: null, version: '6.0', platform: 'unknown' };

/**
 * Parse a `location.hash` into launch parameters. Pure.
 *
 * Telegram's hash is `application/x-www-form-urlencoded`, and its own decoder
 * rewrites `+` to a space before `decodeURIComponent` — which is exactly what
 * `URLSearchParams` does, so we use that rather than reimplementing it.
 *
 * The optional `?` is Telegram's own convention: anything before it is a path
 * segment (`#/somewhere?tgWebAppData=…`), not a parameter. Real clients send no
 * path today, but the SDK handles it and a divergence here would show up as
 * "works everywhere except one client".
 */
export function parseLaunchParams(hash: string): LaunchParams {
  const withoutHash = hash.startsWith('#') ? hash.slice(1) : hash;
  if (withoutHash === '') return ABSENT;

  const questionMark = withoutHash.indexOf('?');
  const query = questionMark >= 0 ? withoutHash.slice(questionMark + 1) : withoutHash;

  const params = new URLSearchParams(query);
  const initData = params.get('tgWebAppData');

  return {
    // An EMPTY tgWebAppData is not a launch. Telegram sends the parameter with
    // no value when it has no user to sign — treating that as a Mini App would
    // send `{"initData":""}` to the server and get a 400 forever.
    initData: initData === null || initData === '' ? null : initData,
    version: params.get('tgWebAppVersion') || ABSENT.version,
    platform: params.get('tgWebAppPlatform') || ABSENT.platform,
  };
}

/** `sessionStorage` that cannot throw. Private-mode Safari does. */
function readStorage(): LaunchParams | null {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const p = parsed as Record<string, unknown>;
    if (typeof p.initData !== 'string' || p.initData === '') return null;
    return {
      initData: p.initData,
      version: typeof p.version === 'string' ? p.version : ABSENT.version,
      platform: typeof p.platform === 'string' ? p.platform : ABSENT.platform,
    };
  } catch {
    return null;
  }
}

function writeStorage(value: LaunchParams): void {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Nothing to do. The in-memory copy still serves this page load; only a
    // reload loses it, and a reload with no hash was never recoverable anyway.
  }
}

let current: LaunchParams = ABSENT;

/**
 * Read the hash once, remember it, and fall back to what a previous load
 * remembered.
 *
 * Idempotent: calling it twice re-reads the hash but cannot lose a value, so
 * an accidental second call in a StrictMode double-render is harmless.
 */
export function captureLaunchParams(): LaunchParams {
  if (typeof window === 'undefined') return ABSENT;

  const fromHash = parseLaunchParams(window.location.hash);
  if (fromHash.initData !== null) {
    current = fromHash;
    writeStorage(fromHash);
    return current;
  }

  // No hash this time: a reload, or an in-app client-side navigation that
  // dropped it. What a previous load stored is the only remaining evidence.
  current = readStorage() ?? ABSENT;
  return current;
}

/**
 * True when this page was opened as a Telegram Mini App.
 *
 * The test is "did we ever see signed launch data", not "does
 * `window.Telegram` exist" — the SDK object exists in a plain browser too, and
 * every one of its methods silently succeeds there while doing nothing.
 */
export function isMiniApp(): boolean {
  return current.initData !== null;
}

/** The raw `initData` to exchange for a session token, or null. */
export function getInitData(): string | null {
  return current.initData;
}

/** Bot API version of the host client; `'6.0'` when it did not say. */
export function getClientVersion(): string {
  return current.version;
}

/** Host platform, or `'unknown'`. */
export function getPlatform(): string {
  return current.platform;
}

/** Test seam. Not used by the app. */
export const __testing = { STORAGE_KEY, ABSENT, reset: () => (current = ABSENT) };
